import { useEffect, useState } from 'react';
import { DataGrid, GridCellEditCommitParams, GridColDef } from '@mui/x-data-grid';
import { Container, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { Brand, Country, deleteCountry, getBrands, getCountries, putCountry, updateCountry, Locale, getLocales } from 'api';
import { generateActionsGridColDef } from 'components/Table/Actions';
import { TableHeader } from 'components/Table/Header';
import { generateLocaleDef } from 'components/Table/Locale';
import { useIntl } from 'react-intl';

export function CountriesPage() {
  const [isLoading, setIsLoading] = useState(true);
  const intl = useIntl();
  const [data, setData] = useState<Partial<Country>[]>([]);
  const [locales, setLocales] = useState<Locale[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [activeBrand, setActiveBrand] = useState<Brand>();

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: intl.formatMessage({ id: 'app.table.heading.id' }),
    },
    {
      field: 'brandId',
      headerName: intl.formatMessage({ id: 'app.table.heading.brandId' }),
    },
    generateLocaleDef({
      field: 'primaryLanguage',
      headerName: intl.formatMessage({ id: 'app.table.heading.primaryLanguage' }),
      locales,
    }),
    generateLocaleDef({
      field: 'secondaryLanguage',
      headerName: intl.formatMessage({ id: 'app.table.heading.secondaryLanguage' }),
      locales,
    }),
    {
      field: 'temperatureUnit',
      headerName: 'Temperature Unit',
      width: 160,
      editable: true,
      renderCell: ({ value = 'C' }) => (
        <>{value === 'F' ? 'Fahrenheit (°F)' : 'Celsius (°C)'}</>
      ),
      renderEditCell: ({ id, field, value, api }) => {
        const setTemperatureUnit = (event: SelectChangeEvent<string>) => {
          api.setEditCellValue({
            id,
            field,
            value: event.target.value as 'C' | 'F',
          });
        };
        return (
          <FormControl fullWidth>
            <Select
              value={typeof value === 'string' ? value : String(value) || 'C'}
              label="Temperature Unit"
              onChange={setTemperatureUnit}
            >
              <MenuItem value="C">Celsius (°C)</MenuItem>
              <MenuItem value="F">Fahrenheit (°F)</MenuItem>
            </Select>
          </FormControl>
        );
      },
    },
    generateActionsGridColDef({
      headerName: intl.formatMessage({id: 'app.table.heading.actions'}),
      onDelete: async (id) => {
        await deleteCountry({ id: id as string, brandId: activeBrand?.id as string });
        setData(data.filter(({ id: countryId }) => countryId !== id));
      }
    }),
  ];

  const onCellEditCommit = async ({ id, field, value }: GridCellEditCommitParams) => {
    await updateCountry({ id: id as string, brandId: activeBrand?.id, [field]: value });
  };
  const onAdd = async ({ id }: { id: string }) => {
    const newRow = { id, brandId: activeBrand?.id, primaryLanguage: 'en', temperatureUnit: 'C' as 'C' };

    await putCountry(newRow);
    setData([...data, newRow]);
  };

  useEffect(() => {
    getBrands().then((brands) => {
      setBrands(brands);
      setActiveBrand(brands[0]);
    })
  }, [setBrands]);

  useEffect(() => {
    if (activeBrand) {
      getCountries().then(results => {
        setData(results.filter(({ brandId }) => brandId === activeBrand.id));
        setIsLoading(false);
      });
    }
  }, [activeBrand, setData, setIsLoading]);

  useEffect(() => {
    getLocales().then(setLocales);
  }, [setLocales]);

  return (
    <Container style={{ marginTop: '80px', minHeight: '300px' }}>
      <TableHeader
        title={`${intl.formatMessage({id: 'app.heading.countries'})} (${activeBrand?.id})`}
        onAdd={onAdd}>
        <>
          {activeBrand && brands?.length > 1 && (
            <FormControl>
              <InputLabel id="brands-select-label">{intl.formatMessage({ id: 'app.heading.brand' })}</InputLabel>
              <Select
                sx={{ marginRight: '7px', minWidth: '100px' }}
                size='small'
                labelId="brands-select-label"
                id="brands-select"
                value={activeBrand?.id}
                label="Brand"
                onChange={(event) => {
                  setActiveBrand(brands.find(({ id }) => id === event.target.value));
                }}
              >
                {brands.map(({ id }) => (
                  <MenuItem key={id} value={id}>{id}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </>
      </TableHeader>
      <DataGrid
        loading={isLoading}
        autoHeight
        rows={data}
        columns={columns}
        onCellEditCommit={onCellEditCommit}
      />
    </Container>
  );
}
